import React from "react";
import { Button } from "../../../components/ui";
import { formatCreatedOn } from "../../brand/planFormat";
import Milestone from "./Milestone";
import { formatMoney } from "../pricing";
import type { PriceBreakdown, SubscriptionPlan } from "../types/billing.types";

/** One line of the breakdown: what it is on the left, what it comes to on the
 *  right. Tabular figures so the column of rupees lines up. */
const Line: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
}> = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4">
    <span className="text-[13px] 3xl:text-[14px] text-ink-muted">{label}</span>
    <span className="tabular-nums text-[13px] 3xl:text-[14px] font-medium text-ink">
      {value}
    </span>
  </div>
);

interface OrderSummaryProps {
  plan: SubscriptionPlan;
  price: PriceBreakdown;
  onPay: () => void;
  /** The purchase is with the server — the button spins and stays pressed. */
  paying?: boolean;
  /**
   * Whether the card carries the button that moves the checkout on.
   *
   * False past the first step: from then on the thing to press belongs to the
   * step being shown — a method to choose, a mandate to send — and a second
   * button over here saying "Pay now" would be a race between two ways
   * forward. The figures stay up throughout, which is the point of the card.
   */
  payable?: boolean;
}

/**
 * What is being bought and what it comes to, beside the form that says who it
 * is billed to.
 *
 * Every figure is worked out by `pricing` rather than written here, and the
 * lines are shown even where they could be inferred: a total on its own is a
 * number to be taken on trust, and the subtotal and the tax are what make it
 * one that can be checked.
 *
 * Nothing here says anything about a coupon. Codes are not taken — neither the
 * quote nor the purchase carries one — so a discount line, applied or empty,
 * would be answering a question this checkout never asks. Where the server
 * takes something off an order of its own accord it is already off
 * `taxableAmount`, which is the figure the subtotal quotes.
 *
 * The two dates under the total are the other half of what is being agreed to:
 * what happens now, and what happens at the end of the cycle. They are stops
 * on one line because that is what they are — the same subscription, at two
 * points in its life.
 */
const OrderSummary: React.FC<OrderSummaryProps> = ({
  plan,
  price,
  onPay,
  paying = false,
  payable = true,
}) => (
  <section
    aria-labelledby="order-summary-heading"
    className="rounded-md border border-[#5BD3E1] bg-white p-5 3xl:p-6"
  >
    <h2
      id="order-summary-heading"
      className="text-[16px] 3xl:text-[18px] font-semibold text-ink"
    >
      Order summary
    </h2>

    <div className="mt-4 flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-[15px] 3xl:text-[16px] font-semibold text-ink">
          {plan.productName}
        </p>
        <p className="mt-0.5 text-[12px] 3xl:text-[13px] text-ink-muted">
          {plan.cycleLabel} plan
        </p>
      </div>
      {/* What the plan is being sold for, not what it is listed at: the price
          the tax is charged on, so the two figures under it add up to the total
          rather than to a number nobody is paying. Where a discount is in force
          it is already off by the time it gets here; see `taxableAmount`. */}
      <span className="tabular-nums text-[15px] 3xl:text-[16px] font-medium text-ink">
        {formatMoney(price.taxableAmount)}
      </span>
    </div>

    <div className="mt-4 flex flex-col gap-2.5 pt-4">
      <Line label="Subtotal" value={formatMoney(price.taxableAmount)} />
      <Line
        label={`GST ${Math.round(price.taxRate * 100)}%`}
        value={formatMoney(price.tax)}
      />
    </div>

    <div className="mt-4 flex items-baseline justify-between gap-4 border-t border-line pt-4">
      <span className="text-[16px] 3xl:text-[18px] font-semibold text-ink">
        Total
      </span>
      <span className="tabular-nums text-[21px] 3xl:text-[24px] font-bold text-ink">
        {formatMoney(price.total)}
      </span>
    </div>

    <ul className="mt-5">
      <Milestone
        done
        title="Today full access unlocked"
        detail={`Get complete access to ${plan.productName}.`}
      />
      <Milestone
        last
        title={`${formatCreatedOn(price.renewsOn)}, plan ends reminder.`}
        detail="We'll email you 7 days before your plan ends, so there are no surprises."
      />
    </ul>

    {payable && (
      <Button
        variant="primary"
        size="lg"
        fullWidth
        className="mt-5"
        loading={paying}
        onClick={onPay}
      >
        {/* The figure on the button itself: what is about to be taken should be
            readable without looking back up at the total, which is the one line
            a buyer checks twice before pressing.

            Two spans rather than one string, so the word and the figure are set
            apart by the button's own gap — the same one it puts between an icon
            and its label — instead of by a single space that runs "Pay" into
            the rupee sign. The accessible name is unaffected: it is still read
            out as one phrase. */}
        <span>Pay</span>
        <span>{formatMoney(price.total)}</span>
      </Button>
    )}
  </section>
);

export default OrderSummary;
